import { Capacitor, registerPlugin } from '@capacitor/core';

/**
 * What a device can do about a screenshot of something it is showing — honestly.
 *
 * - Android (native shell): BLOCK. FLAG_SECURE on the window makes a screenshot or a screen
 *   recording come out black, and hides the app in the task switcher, for as long as it is on.
 * - iOS (native shell): DETECT. Apple gives no block; the OS says a screenshot happened after
 *   the fact, and whether the screen is being recorded or mirrored right now. The view-once
 *   viewer reports the former to the sender and blanks itself during the latter.
 * - A browser: NOTHING. No block, no detection. The viewer falls back to hold-to-view, so the
 *   words are never sitting readable on screen with both hands free.
 *
 * The native halves are app-local Capacitor plugins named ScreenGuard (ScreenGuardPlugin.java,
 * ScreenGuardPlugin.swift). There is deliberately no web implementation: registering one would
 * let the web build claim a capability it doesn't have.
 */
const ScreenGuard = registerPlugin('ScreenGuard');

/** 'block' | 'detect' | 'none' */
export function screenGuardMode() {
  try {
    if (!Capacitor.isNativePlatform()) return 'none';
    const p = Capacitor.getPlatform();
    if (p === 'android') return 'block';
    if (p === 'ios') return 'detect';
  } catch { /* not in a shell */ }
  return 'none';
}

/**
 * Guard the screen for as long as the returned release() is not called.
 *
 * On Android the window goes secure. On iOS `onScreenshot` fires when one is taken and
 * `onCaptureChanged(captured)` when recording/mirroring starts or stops (and once on start,
 * with the current value). Elsewhere this is a no-op that resolves to a release() which does
 * nothing — the caller decides what to do with mode 'none' before it gets here.
 */
export async function guardScreen({ onScreenshot, onCaptureChanged } = {}) {
  const mode = screenGuardMode();
  if (mode === 'none') return () => {};
  const handles = [];
  try {
    if (mode === 'block') {
      await ScreenGuard.enable();
    } else {
      if (onScreenshot) handles.push(await ScreenGuard.addListener('screenshot', () => onScreenshot()));
      if (onCaptureChanged) handles.push(await ScreenGuard.addListener('captureChanged', (e) => onCaptureChanged(!!e?.captured)));
      const r = await ScreenGuard.watch();
      if (onCaptureChanged && r?.captured) onCaptureChanged(true);
    }
  } catch (e) {
    // A shell without the plugin behaves like a browser; the caller already chose hold-to-view
    // only for 'none', so say so loudly in dev rather than quietly dropping the guard.
    console.warn('ScreenGuard unavailable', e);
  }
  let released = false;
  return async () => {
    if (released) return;
    released = true;
    try {
      if (mode === 'block') await ScreenGuard.disable();
      else await ScreenGuard.unwatch();
    } catch { /* already down */ }
    handles.forEach((h) => { try { h.remove(); } catch { /* gone */ } });
  };
}
